import { useCallback, useEffect, useMemo, useState } from "react";
import { BadgePercent, Building2, CreditCard, FileCheck2, Plus, ReceiptText, RefreshCw, RotateCcw, ShieldAlert, WalletCards } from "lucide-react";
import type {
  AddonCatalogItem,
  BillingProfileSnapshot,
  BillingProfileView,
  BillingNotificationView,
  BillingInterval,
  InvoiceView,
  OrdersBillingConsole,
  PaymentTransactionView,
  PlanProductCatalogItem,
  PlanVersionDetail,
  PurchaseOrderDetail,
  PurchaseOrderLedgerItem,
  ReconciliationDiff,
  RefundTransactionView,
  SessionView,
  Tenant,
} from "@control-plane/contracts";
import { billingIntervalLabels, invoicePreferenceLabels, purchaseChannelLabels, taxModeLabels } from "@control-plane/contracts";
import { ApprovalGateModal } from "../components/ApprovalGate";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { InlineFeedback } from "../components/Feedback";
import { Drawer } from "../components/Overlay";
import { PageHeader } from "../components/PageHeader";
import { RemotePageState } from "../components/RemotePageState";
import { SectionCard } from "../components/SectionCard";
import { StatusBadge } from "../components/StatusBadge";
import { V3LedgerQueueBand } from "../components/V3LedgerQueueBand";
import { V3PageTabs, type V3PageTab } from "../components/V3PageTabs";
import { V3Select } from "../components/V3Select";
import { Wizard, type WizardStep } from "../components/Wizard";
import { ApiClientError, api } from "../lib/api";
import { useRemote } from "../lib/useRemote";

const statusOptions = [
  { value: "", label: "全部订单状态" },
  { value: "draft", label: "草稿" },
  { value: "pending_payment", label: "待支付" },
  { value: "pending_offline_confirm", label: "待确认收款" },
  { value: "paid", label: "已收款" },
  { value: "provisioning", label: "开通中" },
  { value: "completed", label: "已完成" },
  { value: "canceled", label: "已取消" },
  { value: "refunded", label: "已退款" },
  { value: "exception", label: "异常" },
];
const channelOptions = (Object.entries(purchaseChannelLabels) as Array<[PurchaseChannel, string]>).map(([value, label]) => ({ value, label }));
const refundHandlingLabels = {
  none: "只登记退款，不处理订阅",
  suspend: "退款后暂停订阅",
  cancel: "退款后取消订阅",
};
const reconciliationScopeOptions = [
  { value: "daily", label: "每日自动口径" },
  { value: "manual", label: "人工复核口径" },
];

type OrdersBillingTab = "orders" | "billing" | "reconciliation";
const ordersBillingTabs: V3PageTab[] = [
  { value: "orders", label: "订购管理", icon: ReceiptText, panelId: "orders-billing-panel-orders" },
  { value: "billing", label: "账务处理", icon: WalletCards, panelId: "orders-billing-panel-billing" },
  { value: "reconciliation", label: "对账回执", icon: RotateCcw, panelId: "orders-billing-panel-reconciliation" },
];

type CreateOrderData = {
  tenants: Tenant[];
  plans: Array<{ productName: string; description: string; versionId: string; version: number; changeNote: string }>;
  addons: Array<{ addonName: string; kindLabel: string; versionId: string; version: number; status: string; billingInterval: BillingInterval; amountMinor: string; currency: string; taxRateBp: number; targetName: string; validFrom: string; validTo: string | null }>;
  discountRules: DiscountRuleView[];
};

type AddonDraft = { versionId: string; quantity: string };
type PurchaseChannel = keyof typeof purchaseChannelLabels;
type DiscountTargetKind = "plan" | "addon";
type DiscountRuleView = {
  id: string;
  tenantId: string;
  tenantName: string;
  planVersionId: string | null;
  addonVersionId: string | null;
  targetName: string;
  billingInterval: BillingInterval | null;
  currency: string;
  discountAmountMinor: string;
  status: "draft" | "pending_approval" | "approved" | "revoked";
  statusLabel: string;
  startsAt: string;
  endsAt: string;
  maxRedemptions: number;
  redeemedCount: number;
  reason: string;
  approvalRequestId: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type InvoicePreference = keyof typeof invoicePreferenceLabels;
type TaxMode = keyof typeof taxModeLabels;

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("zh-CN");
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString("zh-CN");
}

function formatMoney(minor: string, currency: string) {
  const negative = minor.startsWith("-");
  const digits = negative ? minor.slice(1) : minor;
  const padded = digits.padStart(3, "0");
  const yuan = padded.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const cents = padded.slice(-2);
  return `${negative ? "-" : ""}${currency} ${yuan}.${cents}`;
}

function localDateTimeValue() {
  const date = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function localDateTimeOffset(days: number) {
  const date = new Date(Date.now() + days * 86400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toDateTimeLocal(value: string | null | undefined) {
  const source = value ? new Date(value) : new Date();
  if (Number.isNaN(source.getTime())) return localDateTimeValue();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${source.getFullYear()}-${pad(source.getMonth() + 1)}-${pad(source.getDate())}T${pad(source.getHours())}:${pad(source.getMinutes())}`;
}

function localDateValue() {
  const date = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function fromDateTimeLocal(value: string) {
  return new Date(value).toISOString();
}

function isPositiveQuantity(value: string) {
  return /^(?:[1-9][0-9]{0,5})$/.test(value.trim());
}

function yuanToMinor(value: string) {
  const text = value.trim().replace(/,/g, "");
  if (!/^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/.test(text)) return null;
  const [yuan, cents = ""] = text.split(".");
  const minor = `${yuan}${cents.padEnd(2, "0")}`.replace(/^0+(?=\d)/, "");
  if (!/^(0|[1-9][0-9]{0,17})$/.test(minor)) return null;
  return minor;
}

function signedYuanToMinor(value: string) {
  const text = value.trim().replace(/,/g, "");
  const negative = text.startsWith("-");
  const normalized = negative ? text.slice(1) : text;
  const minor = yuanToMinor(normalized);
  if (!minor) return null;
  if (minor === "0") return "0";
  return `${negative ? "-" : ""}${minor}`;
}

function compareMinor(a: string, b: string) {
  return BigInt(a) > BigInt(b) ? 1 : BigInt(a) < BigInt(b) ? -1 : 0;
}

function ruleTargetLabel(rule: DiscountRuleView) {
  return rule.planVersionId ? "套餐优惠" : "加购优惠";
}

function ruleAvailabilityLabel(rule: DiscountRuleView, now = Date.now()) {
  if (rule.status !== "approved") return rule.statusLabel;
  if (new Date(rule.startsAt).getTime() > now) return "未到生效时间";
  if (new Date(rule.endsAt).getTime() <= now) return "已过有效期";
  if (rule.redeemedCount >= rule.maxRedemptions) return "次数已用完";
  return "业务可用";
}

function hasCreateOrderPermission(session?: SessionView) {
  const actions = session?.actions as readonly string[] | undefined;
  return Boolean(actions?.includes("orders.manage") || actions?.includes("orders.create"));
}

function hasAction(session: SessionView | undefined, action: string) {
  return Boolean((session?.actions as readonly string[] | undefined)?.includes(action));
}

function reconciliationObjectLabel(value: string) {
  return ({ order: "订购单", payment: "收款", invoice: "账单", subscription: "订阅", usage: "用量" } as Record<string, string>)[value] ?? "业务对象";
}

function diffTypeLabel(value: string) {
  return ({
    invoice_payment_amount_mismatch: "账单收款金额不一致",
    missing_invoice: "收款缺少账单",
    missing_subscription: "账单缺少订阅",
    status_mismatch: "业务状态不一致",
    refund_amount_mismatch: "退款金额不一致",
  } as Record<string, string>)[value] ?? "差异待确认";
}

function severityLabel(value: string) {
  return ({ low: "低影响", medium: "中影响", high: "高影响" } as Record<string, string>)[value] ?? "影响待确认";
}

function notificationStatusLabel(value: string) {
  return ({ pending: "待发送", sending: "发送中", sent: "已发送", failed: "发送失败", dead: "多次失败" } as Record<string, string>)[value] ?? "状态待确认";
}

export function OrdersBillingPage({ session }: { session?: SessionView }) {
  const [status, setStatus] = useState("");
  const [activeTab, setActiveTab] = useState<OrdersBillingTab>("orders");
  const [moneyLedgerView, setMoneyLedgerView] = useState<"invoices" | "payments" | "refunds">("invoices");
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmOrder, setConfirmOrder] = useState<PurchaseOrderLedgerItem | null>(null);
  const [refundPayment, setRefundPayment] = useState<PaymentTransactionView | null>(null);
  const [invoiceIssue, setInvoiceIssue] = useState<InvoiceView | null>(null);
  const [reconciliationRunOpen, setReconciliationRunOpen] = useState(false);
  const [reconciliationDiff, setReconciliationDiff] = useState<ReconciliationDiff | null>(null);
  const [detailRefreshVersion, setDetailRefreshVersion] = useState(0);
  const loader = useCallback(() => api<OrdersBillingConsole>("/api/orders"), []);
  const remote = useRemote(loader, (data) => data.items.length === 0);
  const rows = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.items : [];
  const visibleRows = status ? rows.filter((row) => row.status === status) : rows;
  const queueCounts = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.queueCounts : null;
  const canConfirmOffline = Boolean((session?.actions as readonly string[] | undefined)?.includes("payments.confirm_offline"));
  const canIssueInvoice = hasAction(session, "invoices.issue");
  const canRequestRefund = hasAction(session, "refunds.request");
  const canRunReconciliation = hasAction(session, "reconciliation.run");
  const canResolveReconciliation = hasAction(session, "reconciliation.resolve");
  const canManageBillingProfiles = hasAction(session, "billing_profiles.manage");

  const orderColumns = useMemo<Array<DataTableColumn<PurchaseOrderLedgerItem>>>(() => [
    { key: "order", header: "订购单", render: (row) => <div className="entity"><span><ReceiptText size={16} /></span><div><strong>{row.orderNo}</strong><small>{row.tenantName} · {row.channelLabel}</small></div></div> },
    { key: "status", header: "订单状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{row.statusLabel}</small></> },
    { key: "amount", header: "应收金额", render: (row) => <span className="app-number">{formatMoney(row.totalMinor, row.currency)}</span> },
    { key: "confirm", header: "收款确认", render: (row) => <span>{row.confirmedAt ? `已确认 · ${formatDateTime(row.confirmedAt)}` : "等待线下确认"}</span> },
    { key: "next", header: "下一步", render: (row) => <span>{row.nextAction}</span> },
    { key: "updated", header: "更新时间", render: (row) => <span>{formatDateTime(row.updatedAt)}</span> },
  ], []);

  const invoiceColumns = useMemo<Array<DataTableColumn<InvoiceView>>>(() => [
    { key: "invoice", header: "账单", render: (row) => <div className="entity"><span><FileCheck2 size={16} /></span><div><strong>{row.invoiceNo}</strong><small>{row.tenantName}</small></div></div> },
    { key: "status", header: "账单状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{row.statusLabel}</small></> },
    { key: "amount", header: "账单金额", render: (row) => <span className="app-number">{formatMoney(row.totalMinor, row.currency)}</span> },
    { key: "paid", header: "已收金额", render: (row) => <span className="app-number">{formatMoney(row.amountPaidMinor, row.currency)}</span> },
    { key: "due", header: "截止时间", render: (row) => <span>{formatDateTime(row.dueAt)}</span> },
  ], []);

  const paymentColumns = useMemo<Array<DataTableColumn<PaymentTransactionView>>>(() => [
    { key: "tenant", header: "客户", render: (row) => <span>{row.tenantName}</span> },
    { key: "method", header: "收款方式", render: (row) => <span>{row.methodLabel}</span> },
    { key: "status", header: "到账状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{row.statusLabel}</small></> },
    { key: "amount", header: "收款金额", render: (row) => <span className="app-number">{formatMoney(row.amountMinor, row.currency)}</span> },
    { key: "occurred", header: "发生时间", render: (row) => <span>{formatDateTime(row.occurredAt)}</span> },
  ], []);

  const refundColumns = useMemo<Array<DataTableColumn<RefundTransactionView>>>(() => [
    { key: "tenant", header: "客户", render: (row) => <span>{row.tenantName}</span> },
    { key: "status", header: "退款状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{row.statusLabel}</small></> },
    { key: "amount", header: "退款金额", render: (row) => <span className="app-number">{formatMoney(row.amountMinor, row.currency)}</span> },
    { key: "reason", header: "退款原因", render: (row) => <span>{row.failureMessage ?? row.reason}</span> },
    { key: "requested", header: "申请时间", render: (row) => <span>{formatDateTime(row.requestedAt)}</span> },
  ], []);

  const notificationColumns = useMemo<Array<DataTableColumn<BillingNotificationView>>>(() => [
    { key: "tenant", header: "企业", render: (row) => <span>{row.tenantName ?? "平台记录"}</span> },
    { key: "subject", header: "通知内容", render: (row) => <span>{row.subject}</span> },
    { key: "status", header: "投递状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{notificationStatusLabel(row.status)}</small></> },
    { key: "attempts", header: "重试记录", render: (row) => <span><strong className="app-number">{row.attempts}</strong> 次</span> },
    { key: "receipt", header: "回执", render: (row) => <span>{row.sentAt ? `已发送 · ${formatDateTime(row.sentAt)}` : row.lastError ? `失败：${row.lastError}` : "等待投递"}</span> },
  ], []);

  const reconciliationColumns = useMemo<Array<DataTableColumn<ReconciliationDiff>>>(() => [
    { key: "object", header: "对象", render: (row) => <span>{reconciliationObjectLabel(row.objectType)}</span> },
    { key: "type", header: "差异类型", render: (row) => <span>{diffTypeLabel(row.diffType)}</span> },
    { key: "expected", header: "系统应有", render: (row) => <span>{row.expected}</span> },
    { key: "actual", header: "实际记录", render: (row) => <span>{row.actual}</span> },
    { key: "severity", header: "影响", render: (row) => <span>{severityLabel(row.severity)}</span> },
    { key: "created", header: "发现时间", render: (row) => <span>{formatDateTime(row.createdAt)}</span> },
  ], []);

  return <div className="page orders-billing-page">
    <PageHeader
      icon={WalletCards}
      title="订购与账单"
      meta={<span>线下确认收款模式，统一查看订购单、账单、收款、退款与对账状态</span>}
      actions={<>
        <button className="button secondary" onClick={remote.reload}><RefreshCw size={16} />刷新</button>
        {hasCreateOrderPermission(session) && <button className="button primary" onClick={() => setShowCreate(true)}><Plus size={17} />新建订购</button>}
      </>}
      navigation={<V3PageTabs tabs={ordersBillingTabs} active={activeTab} onChange={(value) => setActiveTab(value as OrdersBillingTab)} ariaLabel="订购与账单页内视图" />}
    />

    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      renderEmptyData
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能查看订购与账单", description: "请联系平台负责人开通订购读取权限；页面不会用空记录冒充真实状态。" } : null}
      emptyHint="当前没有订购单，创建订购单后会在这里看到收款、账单和对账状态。"
    >
      {(data) => <div className="orders-billing-stack">
        <div id="orders-billing-panel-orders" role="tabpanel" aria-labelledby="v3-page-tab-orders" className="orders-billing-tab-panel" hidden={activeTab !== "orders"}>
          <SectionCard
          icon={ReceiptText}
          title="订购单台账"
          meta={<span><strong className="app-number">{visibleRows.length}</strong> 条</span>}
          variant="ledger"
          filters={<div className="ui-ledger-filters orders-billing-filters">
            {statusOptions.map((option) => <button
              key={option.value || "all"}
              type="button"
              className={status === option.value ? "button secondary active" : "button ghost"}
              onClick={() => setStatus(option.value)}
            >
              <span>{option.label}</span>
              {option.value && <strong className="app-number">{queueCounts?.[option.value as keyof OrdersBillingConsole["queueCounts"]] ?? 0}</strong>}
            </button>)}
          </div>}
        >
          <DataTable
            columns={orderColumns}
            rows={visibleRows}
            rowKey={(row) => row.id}
            ariaLabel="订购单台账"
            empty={<span>没有匹配的订购单。</span>}
            rowActions={(row) => <div className="row-actions">
              <button className="button ghost" onClick={() => setSelectedOrderId(row.id)}>查看详情</button>
              {canConfirmOffline && row.status === "pending_offline_confirm" && <button className="button secondary" onClick={() => setConfirmOrder(row)}><ShieldAlert size={15} />确认收款</button>}
            </div>}
          />
          </SectionCard>

          <DiscountRulesSection session={session} />
        </div>

        <div id="orders-billing-panel-billing" role="tabpanel" aria-labelledby="v3-page-tab-billing" className="orders-billing-tab-panel" hidden={activeTab !== "billing"}>
          {canManageBillingProfiles && <BillingProfilesSection session={session} />}

          <SectionCard icon={CreditCard} title="线下确认收款模式" meta={<span>{data.paymentMode === "offline_confirm" ? "已启用" : "需确认"}</span>}>
            <div className="detail-grid">
              <span><strong>当前模式</strong>线下确认收款模式</span>
              <span><strong>待确认收款</strong><span className="app-number">{data.queueCounts.pending_offline_confirm ?? 0}</span> 单</span>
              <span><strong>最近对账</strong>{data.reconciliation.latestRun ? `${data.reconciliation.latestRun.diffCount} 项差异` : "暂无对账记录"}</span>
              <span><strong>待处理差异</strong><span className="app-number">{data.reconciliation.diffs.length}</span> 项</span>
            </div>
          </SectionCard>

          <SectionCard icon={FileCheck2} title="账单与收款" variant="ledger">
          <V3LedgerQueueBand
            ariaLabel="账单与收款视图"
            value={moneyLedgerView}
            onChange={setMoneyLedgerView}
            items={[
              { key: "invoices", label: "账单", count: data.invoices.length, icon: FileCheck2 },
              { key: "payments", label: "收款", count: data.payments.length, icon: WalletCards },
              { key: "refunds", label: "退款", count: data.refunds.length, icon: RotateCcw },
            ]}
          />
          <div className="ledger-view" hidden={moneyLedgerView !== "invoices"}>
            <DataTable
              columns={invoiceColumns}
              rows={data.invoices}
              rowKey={(row) => row.id}
              ariaLabel="账单列表"
              empty={<span>暂无账单。</span>}
              rowActions={(row) => <div className="row-actions">
                {canIssueInvoice && row.status === "draft" ? <button className="button secondary" onClick={() => setInvoiceIssue(row)}>签发账单</button> : <span className="muted-action">查看</span>}
              </div>}
            />
          </div>
          <div className="ledger-view" hidden={moneyLedgerView !== "payments"}>
            <DataTable
              columns={paymentColumns}
              rows={data.payments}
              rowKey={(row) => row.id}
              ariaLabel="收款记录"
              empty={<span>暂无收款记录。</span>}
              rowActions={(row) => <div className="row-actions">
                {canRequestRefund && row.status === "succeeded" ? <button className="button secondary" onClick={() => setRefundPayment(row)}>申请退款</button> : <span className="muted-action">查看</span>}
              </div>}
            />
          </div>
          <div className="ledger-view" hidden={moneyLedgerView !== "refunds"}>
            <DataTable columns={refundColumns} rows={data.refunds} rowKey={(row) => row.id} ariaLabel="退款记录" empty={<span>暂无退款记录。</span>} />
          </div>
          </SectionCard>
        </div>

        <div id="orders-billing-panel-reconciliation" role="tabpanel" aria-labelledby="v3-page-tab-reconciliation" className="orders-billing-tab-panel" hidden={activeTab !== "reconciliation"}>
          <SectionCard
          icon={RotateCcw}
          title="每日对账"
          variant="ledger"
          filters={<div className="reconciliation-summary reconciliation-summary-inline">
            <span><strong>对账状态</strong>{data.reconciliation.latestRun ? <StatusBadge value={data.reconciliation.latestRun.status} /> : "暂无记录"}</span>
            <span><strong>检查订单</strong><span><b className="app-number">{data.reconciliation.latestRun?.checkedOrders ?? 0}</b> 单</span></span>
            <span><strong>检查收款</strong><span><b className="app-number">{data.reconciliation.latestRun?.checkedPayments ?? 0}</b> 笔</span></span>
            <span><strong>检查账单</strong><span><b className="app-number">{data.reconciliation.latestRun?.checkedInvoices ?? 0}</b> 张</span></span>
          </div>}
          action={canRunReconciliation && <button className="button primary reconciliation-run-button" onClick={() => setReconciliationRunOpen(true)}><RefreshCw size={15} />运行对账</button>}
        >
          <DataTable
            columns={reconciliationColumns}
            rows={data.reconciliation.diffs}
            rowKey={(row) => row.id}
            ariaLabel="对账差异"
            empty={<span>当前没有待处理对账差异。</span>}
            rowActions={(row) => <div className="row-actions">
              {canResolveReconciliation ? <button className="button secondary" onClick={() => setReconciliationDiff(row)}>解决差异</button> : <span className="muted-action">查看</span>}
            </div>}
          />
          </SectionCard>

          <SectionCard icon={FileCheck2} title="通知回执" description="只读展示真实通知状态、重试次数和回执；待发送不会显示为已发送。">
            <DataTable columns={notificationColumns} rows={data.notifications} rowKey={(row) => row.id} ariaLabel="通知回执" empty={<span>暂无通知记录。</span>} />
          </SectionCard>
        </div>
      </div>}
    </RemotePageState>

    {selectedOrderId && <OrderDetailDrawer key={`${selectedOrderId}-${detailRefreshVersion}`} session={session} orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} onChanged={() => { setDetailRefreshVersion((version) => version + 1); void remote.reload(); }} />}
    {showCreate && <CreateOrderWizard onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); void remote.reload(); }} />}
    {confirmOrder && session && <OfflineConfirmFlow session={session} order={confirmOrder} onClose={() => setConfirmOrder(null)} onSaved={() => { setConfirmOrder(null); setDetailRefreshVersion((version) => version + 1); void remote.reload(); }} />}
    {refundPayment && <RefundRequestFlow payment={refundPayment} onClose={() => setRefundPayment(null)} onSaved={() => { setRefundPayment(null); void remote.reload(); }} />}
    {invoiceIssue && <InvoiceIssueFlow invoice={invoiceIssue} onClose={() => setInvoiceIssue(null)} onSaved={() => { setInvoiceIssue(null); void remote.reload(); }} />}
    {reconciliationRunOpen && <ReconciliationRunFlow onClose={() => setReconciliationRunOpen(false)} onSaved={() => { setReconciliationRunOpen(false); void remote.reload(); }} />}
    {reconciliationDiff && <ReconciliationResolveFlow diff={reconciliationDiff} onClose={() => setReconciliationDiff(null)} onSaved={() => { setReconciliationDiff(null); void remote.reload(); }} />}
  </div>;
}

function RefundRequestFlow({ payment, onClose, onSaved }: { payment: PaymentTransactionView; onClose: () => void; onSaved: () => void }) {
  const [amountText, setAmountText] = useState("");
  const [subscriptionHandling, setSubscriptionHandling] = useState<keyof typeof refundHandlingLabels>("none");
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `refund-request-${crypto.randomUUID()}`);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const amountMinor = yuanToMinor(amountText);
  const canSubmit = Boolean(amountMinor && amountMinor !== "0" && compareMinor(amountMinor, payment.amountMinor) <= 0 && reason.trim().length >= 8);

  async function submit() {
    if (!canSubmit || !amountMinor) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api<PurchaseOrderDetail>("/api/refunds", {
        method: "POST",
        body: JSON.stringify({ paymentTransactionId: payment.id, amountMinor, subscriptionHandling, reason, idempotencyKey }),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能申请退款，请联系平台负责人开通退款申请权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新收款记录后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "退款申请失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return <Drawer title="申请退款" description={`${payment.tenantName} · ${payment.orderNo ?? payment.invoiceNo ?? "收款记录"} · ${formatMoney(payment.amountMinor, payment.currency)}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!canSubmit || submitting} onClick={() => void submit()}>{submitting ? "正在提交…" : "提交退款申请"}</button></div>}>
    <div className="form-grid">
      <div className="full"><InlineFeedback tone="info">只能选择真实已到账收款申请退款；提交后进入审核流程，不会直接显示退款成功。</InlineFeedback></div>
      <label><span>退款金额（元）</span><input className="input" inputMode="decimal" value={amountText} onChange={(event) => setAmountText(event.target.value)} placeholder="例如 99.00" /></label>
      <label><span>订阅影响</span><V3Select value={subscriptionHandling} onChange={(value) => setSubscriptionHandling(value as keyof typeof refundHandlingLabels)} options={Object.entries(refundHandlingLabels).map(([value, label]) => ({ value, label }))} ariaLabel="选择退款后的订阅影响" /></label>
      <label className="full"><span>退款原因</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明退款依据，至少 8 个字。" /></label>
    </div>
    <div aria-live="polite">
      {amountText && (!amountMinor || amountMinor === "0") && <InlineFeedback tone="danger">请填写大于 0 的退款金额，最多保留 2 位小数。</InlineFeedback>}
      {amountMinor && compareMinor(amountMinor, payment.amountMinor) > 0 && <InlineFeedback tone="danger">退款金额不能超过这笔真实收款。</InlineFeedback>}
      {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
    </div>
  </Drawer>;
}

function InvoiceIssueFlow({ invoice, onClose, onSaved }: { invoice: InvoiceView; onClose: () => void; onSaved: () => void }) {
  const [periodStart, setPeriodStart] = useState(toDateTimeLocal(invoice.periodStart));
  const [periodEnd, setPeriodEnd] = useState(toDateTimeLocal(invoice.periodEnd));
  const [dueAt, setDueAt] = useState(toDateTimeLocal(invoice.dueAt ?? invoice.periodEnd));
  const [idempotencyKey] = useState(() => `invoice-issue-${crypto.randomUUID()}`);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const windowValid = periodStart && periodEnd && !Number.isNaN(new Date(periodStart).getTime()) && !Number.isNaN(new Date(periodEnd).getTime()) && new Date(periodEnd).getTime() > new Date(periodStart).getTime();
  const dueValid = dueAt && !Number.isNaN(new Date(dueAt).getTime());

  async function submit() {
    if (!windowValid || !dueValid) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api<InvoiceView>(`/api/invoices/${invoice.id}/issue`, {
        method: "POST",
        body: JSON.stringify({ periodStart: fromDateTimeLocal(periodStart), periodEnd: fromDateTimeLocal(periodEnd), dueAt: fromDateTimeLocal(dueAt), idempotencyKey }),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能签发账单，请联系平台负责人开通账单签发权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新账单状态后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "账单签发失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return <Drawer title="签发账单" description={`${invoice.tenantName} · ${invoice.invoiceNo} · ${formatMoney(invoice.totalMinor, invoice.currency)}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!windowValid || !dueValid || submitting} onClick={() => void submit()}>{submitting ? "正在签发…" : "签发账单"}</button></div>}>
    <div className="form-grid">
      <div className="full"><InlineFeedback tone="info">只有草稿账单可以签发；已出账金额不会被覆盖，后续变化必须走账单调整。</InlineFeedback></div>
      <label><span>账期开始</span><input className="input" type="datetime-local" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></label>
      <label><span>账期结束</span><input className="input" type="datetime-local" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></label>
      <label><span>付款截止</span><input className="input" type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} /></label>
    </div>
    <div aria-live="polite">
      {!windowValid && <InlineFeedback tone="danger">账期结束时间必须晚于开始时间。</InlineFeedback>}
      {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
    </div>
  </Drawer>;
}

function ReconciliationRunFlow({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [runDate, setRunDate] = useState(localDateValue());
  const [scope, setScope] = useState<"daily" | "manual">("daily");
  const [idempotencyKey] = useState(() => `reconciliation-run-${crypto.randomUUID()}`);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(runDate);

  async function submit() {
    if (!dateValid) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api("/api/reconciliation-runs", {
        method: "POST",
        body: JSON.stringify({ runDate, scope, idempotencyKey }),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能运行对账，请联系平台负责人开通对账权限。已填写内容仍保留。");
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "对账运行失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return <Drawer title="运行每日对账" description="按选择日期和业务范围检查订购、收款、账单与订阅记录。" onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!dateValid || submitting} onClick={() => void submit()}>{submitting ? "正在对账…" : "运行对账"}</button></div>}>
    <div className="form-grid">
      <label><span>对账日期</span><input className="input" type="date" value={runDate} onChange={(event) => setRunDate(event.target.value)} /></label>
      <label><span>业务范围</span><V3Select value={scope} onChange={(value) => setScope(value as "daily" | "manual")} options={reconciliationScopeOptions} ariaLabel="选择对账业务范围" /></label>
    </div>
    <div aria-live="polite">
      {!dateValid && <InlineFeedback tone="danger">请选择正确的对账日期。</InlineFeedback>}
      {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
    </div>
  </Drawer>;
}

function ReconciliationResolveFlow({ diff, onClose, onSaved }: { diff: ReconciliationDiff; onClose: () => void; onSaved: () => void }) {
  const [status, setStatus] = useState<"resolved" | "ignored">("resolved");
  const [resolutionNote, setResolutionNote] = useState("");
  const [idempotencyKey] = useState(() => `reconciliation-resolve-${crypto.randomUUID()}`);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const canSubmit = resolutionNote.trim().length >= 8;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api(`/api/reconciliation-diffs/${diff.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ status, resolutionNote, idempotencyKey }),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能处置对账差异，请联系平台负责人开通对账处置权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新对账差异后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "对账差异处置失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  }

  return <>
    <Drawer title="解决对账差异" description={`${reconciliationObjectLabel(diff.objectType)} · ${diffTypeLabel(diff.diffType)}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!canSubmit || submitting} onClick={() => setConfirmOpen(true)}>确认处置</button></div>}>
      <div className="form-grid">
        <div className="full reconciliation-diff-evidence">
          <span><strong>系统应有</strong>{diff.expected}</span>
          <span><strong>实际记录</strong>{diff.actual}</span>
        </div>
        <label><span>处置结果</span><V3Select value={status} onChange={(value) => setStatus(value as "resolved" | "ignored")} options={[{ value: "resolved", label: "已解决" }, { value: "ignored", label: "确认忽略" }]} ariaLabel="选择对账差异处置结果" /></label>
        <label className="full"><span>处置理由</span><textarea className="input" rows={3} value={resolutionNote} onChange={(event) => setResolutionNote(event.target.value)} placeholder="说明核对证据和处置结论，至少 8 个字。" /></label>
      </div>
      <div aria-live="polite">
        {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
      </div>
    </Drawer>
    {confirmOpen && <ConfirmDialog
      title="确认处置对账差异"
      description="此操作只更新差异处理状态，不会删除历史记录。"
      confirmLabel="确认处置"
      onConfirm={() => void submit()}
      onClose={() => setConfirmOpen(false)}
      busy={submitting}
    >
      <p className="confirm-copy">请确认已经核对业务证据。处置后列表会重新读取最新状态。</p>
    </ConfirmDialog>}
  </>;
}

function DiscountRulesSection({ session }: { session: SessionView | undefined }) {
  const [showCreate, setShowCreate] = useState(false);
  const [approvalRule, setApprovalRule] = useState<DiscountRuleView | null>(null);
  const loader = useCallback(() => api<{ items: DiscountRuleView[] }>("/api/discount-rules"), []);
  const remote = useRemote(loader, (data) => data.items.length === 0);
  const rows = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.items : [];
  const canCreate = hasCreateOrderPermission(session);
  const columns = useMemo<Array<DataTableColumn<DiscountRuleView>>>(() => [
    { key: "tenant", header: "企业", render: (row) => <span>{row.tenantName}</span> },
    { key: "target", header: "优惠对象", render: (row) => <span>{ruleTargetLabel(row)} · {row.targetName}</span> },
    { key: "interval", header: "周期", render: (row) => <span>{row.billingInterval ? billingIntervalLabels[row.billingInterval] : "全部周期"}</span> },
    { key: "amount", header: "优惠金额", render: (row) => <span className="app-number">{formatMoney(row.discountAmountMinor, row.currency)}</span> },
    { key: "window", header: "有效期", render: (row) => <span>{formatDate(row.startsAt)} 至 {formatDate(row.endsAt)}</span> },
    { key: "usage", header: "使用次数", render: (row) => <span><strong className="app-number">{row.redeemedCount}</strong> / <strong className="app-number">{row.maxRedemptions}</strong></span> },
    { key: "status", header: "业务可用状态", render: (row) => <><StatusBadge value={row.status} /><small className="block">{ruleAvailabilityLabel(row)}</small></> },
  ], []);

  return <SectionCard
    icon={BadgePercent}
    title="合同优惠规则"
    meta={<span><strong className="app-number">{rows.length}</strong> 条</span>}
    variant="ledger"
    filters={<div className="ui-ledger-filters discount-rule-actions">
      <button className="button secondary" onClick={remote.reload}><RefreshCw size={15} />刷新</button>
      {canCreate && <button className="button primary" onClick={() => setShowCreate(true)}><Plus size={16} />新建合同优惠</button>}
    </div>}
  >
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能查看合同优惠", description: "请联系平台负责人开通订购读取权限；页面不会用空白优惠代替真实规则。" } : null}
      emptyHint="当前没有合同优惠规则。需要优惠时先创建规则，审批通过后才能用于订购单。"
    >
      {(data) => <DataTable
        columns={columns}
        rows={data.items}
        rowKey={(row) => row.id}
        ariaLabel="合同优惠规则"
        empty={<span>当前没有合同优惠规则。</span>}
        rowActions={(row) => <div className="row-actions">
          {canCreate && row.status === "pending_approval" && session
            ? <button className="button secondary" onClick={() => setApprovalRule(row)}><ShieldAlert size={15} />创建或选择审批</button>
            : <span className="muted-action">{row.status === "approved" ? "已完成批准" : "暂无操作"}</span>}
        </div>}
      />}
    </RemotePageState>
    {showCreate && <DiscountRuleWizard onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); void remote.reload(); }} />}
    {approvalRule && session && <DiscountRuleApprovalFlow
      session={session}
      rule={approvalRule}
      onClose={() => setApprovalRule(null)}
      onSaved={() => { setApprovalRule(null); void remote.reload(); }}
    />}
  </SectionCard>;
}

function billingConfiguredLabel(profile: BillingProfileView | BillingProfileSnapshot | null | undefined) {
  if (!profile || !profile.configured) return "未配置开票资料";
  return `${profile.companyLegalName || "未填写抬头"} · ${profile.invoicePreferenceLabel} · ${profile.taxModeLabel} ${profile.taxRateBp / 100}%`;
}

function BillingProfileEvidence({ profile }: { profile: BillingProfileView | BillingProfileSnapshot | null | undefined }) {
  if (!profile || !profile.configured) {
    return <InlineFeedback tone="info">当前没有开票资料；新订购单会明确记录“未配置”，不会编造公司抬头或税号。</InlineFeedback>;
  }
  return <div className="detail-grid billing-profile-evidence">
    <span><strong>公司抬头</strong>{profile.companyLegalName || "未填写"}</span>
    <span><strong>发票类型</strong>{profile.invoicePreferenceLabel}</span>
    <span><strong>税务口径</strong>{profile.taxModeLabel} · 税率 <span className="app-number">{profile.taxRateBp / 100}%</span></span>
    <span><strong>税号</strong>{profile.taxNumberMasked || "未填写"}</span>
    <span><strong>账单联系人</strong>{profile.billingContactName || "未填写"}</span>
    <span><strong>联系邮箱</strong>{profile.billingContactEmailMasked || "未填写"}</span>
    <span><strong>联系电话</strong>{profile.billingContactPhoneMasked || "未填写"}</span>
    <span><strong>开户银行</strong>{profile.bankName || "未填写"}</span>
    <span><strong>银行账号</strong>{profile.bankAccountMask || "未填写"}</span>
    <span><strong>资料时间</strong>{formatDateTime(profile.updatedAt)}</span>
  </div>;
}

function BillingProfilesSection({ session }: { session: SessionView | undefined }) {
  const [tenantId, setTenantId] = useState("");
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const loader = useCallback(() => api<{ items: Tenant[] }>("/api/tenants"), []);
  const remote = useRemote(loader, (data) => data.items.length === 0);
  const tenants = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data.items : [];
  const selectedTenant = tenants.find((tenant) => tenant.id === tenantId) ?? null;
  const canManage = hasAction(session, "billing_profiles.manage");

  useEffect(() => {
    if (!tenantId && tenants[0]) setTenantId(tenants[0].id);
  }, [tenantId, tenants]);

  return <SectionCard
    icon={Building2}
    title="开票资料与账单联系人"
    meta={<span>{selectedTenant ? selectedTenant.name : "按企业维护"}</span>}
    variant="ledger"
    filters={<div className="ui-ledger-filters discount-rule-actions">
      <button className="button secondary" onClick={remote.reload}><RefreshCw size={15} />刷新企业</button>
      {canManage && <button className="button primary" disabled={!selectedTenant} onClick={() => selectedTenant && setEditingTenantId(selectedTenant.id)}><Building2 size={16} />维护资料</button>}
    </div>}
  >
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能读取企业列表", description: "请联系平台负责人开通企业读取权限；页面不会让你手填企业编号。" } : null}
      emptyHint="当前没有可维护开票资料的企业。"
    >
      {(data) => <div className="form-grid">
        <label className="full"><span>企业</span><V3Select value={tenantId} onChange={setTenantId} options={data.items.map((tenant) => ({ value: tenant.id, label: tenant.name, hint: tenant.status === "active" ? "运行中" : "状态需确认" }))} placeholder="选择企业" ariaLabel="选择开票资料企业" /></label>
        <div className="full"><InlineFeedback tone="info">开票资料会在创建订购单时自动锁定到订单快照；以后修改资料不会改写旧订单或旧账单。</InlineFeedback></div>
        {!canManage && <div className="full"><InlineFeedback tone="danger">当前账号不能维护开票资料，请联系平台负责人开通账单资料维护权限。</InlineFeedback></div>}
      </div>}
    </RemotePageState>
    {editingTenantId && <BillingProfileDrawer tenantId={editingTenantId} onClose={() => setEditingTenantId(null)} onSaved={() => setEditingTenantId(null)} />}
  </SectionCard>;
}

function BillingProfileDrawer({ tenantId, onClose, onSaved }: { tenantId: string; onClose: () => void; onSaved: () => void }) {
  const loader = useCallback(() => api<BillingProfileView>(`/api/billing-profiles/${tenantId}`), [tenantId]);
  const remote = useRemote(loader);
  return <Drawer title="维护开票资料" description="只保存账单和开票所需信息，旧订单与旧账单快照不会被改写。" onClose={onClose} footer={<button className="button secondary" onClick={onClose}>关闭</button>}>
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能维护开票资料", description: "请联系平台负责人开通账单资料维护权限；已保存资料不会在页面回显完整敏感信息。" } : null}
      emptyHint="当前企业暂无开票资料，可填写后保存。"
    >
      {(profile) => <BillingProfileForm tenantId={tenantId} profile={profile} onSaved={onSaved} />}
    </RemotePageState>
  </Drawer>;
}

function BillingProfileForm({ tenantId, profile, onSaved }: { tenantId: string; profile: BillingProfileView; onSaved: () => void }) {
  const [companyLegalName, setCompanyLegalName] = useState(profile.editable.companyLegalName);
  const [invoicePreference, setInvoicePreference] = useState<InvoicePreference>(profile.editable.invoicePreference);
  const [taxMode, setTaxMode] = useState<TaxMode>(profile.editable.taxMode);
  const [taxRatePercent, setTaxRatePercent] = useState(String(profile.editable.taxRateBp / 100));
  const [taxNumber, setTaxNumber] = useState("");
  const [billingContactName, setBillingContactName] = useState(profile.editable.billingContactName);
  const [billingContactEmail, setBillingContactEmail] = useState("");
  const [billingContactPhone, setBillingContactPhone] = useState("");
  const [billingAddress, setBillingAddress] = useState(profile.editable.billingAddress);
  const [bankName, setBankName] = useState(profile.editable.bankName);
  const [bankAccountMask, setBankAccountMask] = useState(profile.editable.bankAccountMask);
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `billing-profile-save-${crypto.randomUUID()}`);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const parsedTaxRate = /^\d{1,3}(?:\.\d{1,2})?$/.test(taxRatePercent.trim())
    ? Math.round(Number(taxRatePercent.trim()) * 100)
    : Number.NaN;
  const canSubmit = companyLegalName.trim().length > 0 && Number.isInteger(parsedTaxRate) && parsedTaxRate >= 0 && parsedTaxRate <= 10_000 && reason.trim().length >= 8;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError("");
    const body: Record<string, unknown> = {
      companyLegalName,
      billingContactName,
      billingAddress,
      invoicePreference,
      bankName,
      bankAccountMask,
      taxMode,
      taxRateBp: parsedTaxRate,
      reason,
      idempotencyKey,
    };
    if (taxNumber.trim()) body.taxNumber = taxNumber.trim();
    if (billingContactEmail.trim()) body.billingContactEmail = billingContactEmail.trim();
    if (billingContactPhone.trim()) body.billingContactPhone = billingContactPhone.trim();
    try {
      await api<BillingProfileView>(`/api/billing-profiles/${tenantId}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能保存开票资料，请联系平台负责人开通账单资料维护权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 400) {
        setSubmitError(`${failure.message}。请检查账号是否已做掩码，已填写内容仍保留。`);
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。已填写内容仍保留，请刷新资料后再保存。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "开票资料保存失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return <div className="billing-profile-form">
    <BillingProfileEvidence profile={profile} />
    <div className="form-grid">
      <label><span>公司抬头</span><input className="input" value={companyLegalName} onChange={(event) => setCompanyLegalName(event.target.value)} placeholder="填写营业执照上的公司名称" /></label>
      <label><span>发票类型</span><V3Select value={invoicePreference} onChange={(value) => setInvoicePreference(value as InvoicePreference)} options={(Object.entries(invoicePreferenceLabels) as Array<[InvoicePreference, string]>).map(([value, label]) => ({ value, label }))} ariaLabel="选择发票类型" /></label>
      <label><span>税务口径</span><V3Select value={taxMode} onChange={(value) => setTaxMode(value as TaxMode)} options={(Object.entries(taxModeLabels) as Array<[TaxMode, string]>).map(([value, label]) => ({ value, label }))} ariaLabel="选择税务口径" /></label>
      <label><span>税率（%）</span><input className="input app-number" inputMode="decimal" value={taxRatePercent} onChange={(event) => setTaxRatePercent(event.target.value)} placeholder="例如 6" /></label>
      <label><span>新税号</span><input className="input" value={taxNumber} onChange={(event) => setTaxNumber(event.target.value)} placeholder={profile.taxNumberMasked ? "留空保持当前税号" : "填写后保存为脱敏展示"} /></label>
      <label><span>账单联系人</span><input className="input" value={billingContactName} onChange={(event) => setBillingContactName(event.target.value)} placeholder="填写对账联系人姓名" /></label>
      <label><span>新联系邮箱</span><input className="input" value={billingContactEmail} onChange={(event) => setBillingContactEmail(event.target.value)} placeholder={profile.billingContactEmailMasked ? "留空保持当前邮箱" : "填写账单联系人邮箱"} /></label>
      <label><span>新联系电话</span><input className="input" value={billingContactPhone} onChange={(event) => setBillingContactPhone(event.target.value)} placeholder={profile.billingContactPhoneMasked ? "留空保持当前电话" : "填写账单联系人电话"} /></label>
      <label><span>开户银行</span><input className="input" value={bankName} onChange={(event) => setBankName(event.target.value)} placeholder="填写开户行名称" /></label>
      <label><span>银行账号末尾</span><input className="input app-number" value={bankAccountMask} onChange={(event) => setBankAccountMask(event.target.value)} placeholder="例如 ****1234，仅保存隐藏后的样式" /></label>
      <label className="full"><span>账单地址</span><textarea className="input" rows={3} value={billingAddress} onChange={(event) => setBillingAddress(event.target.value)} placeholder="填写开票或账单寄送地址" /></label>
      <label className="full"><span>保存原因</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次维护依据，至少 8 个字。" /></label>
    </div>
    <div aria-live="polite">
      {!Number.isInteger(parsedTaxRate) || parsedTaxRate < 0 || parsedTaxRate > 10_000 ? <InlineFeedback tone="danger">税率请填写 0% 到 100%，最多保留两位小数。</InlineFeedback> : null}
      {bankAccountMask.replace(/\D/g, "").length > 8 && !/[＊*]/.test(bankAccountMask) && <InlineFeedback tone="danger">银行账号只能保存掩码，例如 ****1234，不能保存完整账号。</InlineFeedback>}
      {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
    </div>
    <div className="inline-actions">
      <button className="button primary" disabled={!canSubmit || submitting} onClick={() => void submit()}>{submitting ? "正在保存…" : "保存开票资料"}</button>
    </div>
  </div>;
}

function discountRuleApprovalPayload(rule: DiscountRuleView) {
  return {
    discountRuleId: rule.id,
    tenantId: rule.tenantId,
    planVersionId: rule.planVersionId,
    addonVersionId: rule.addonVersionId,
    billingInterval: rule.billingInterval,
    currency: rule.currency,
    discountAmountMinor: rule.discountAmountMinor,
    startsAt: rule.startsAt,
    endsAt: rule.endsAt,
    maxRedemptions: rule.maxRedemptions,
  };
}

function DiscountRuleApprovalFlow({ session, rule, onClose, onSaved }: { session: SessionView; rule: DiscountRuleView; onClose: () => void; onSaved: () => void }) {
  const [submitError, setSubmitError] = useState("");
  const [idempotencyKey] = useState(() => `discount-rule-approve-${crypto.randomUUID()}`);
  async function execute(approvalId: string, reason: string) {
    setSubmitError("");
    try {
      await api<DiscountRuleView>(`/api/discount-rules/${rule.id}/approve`, {
        method: "POST",
        body: JSON.stringify({ approvalId, reason, idempotencyKey }),
      });
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能批准合同优惠，请联系平台负责人开通订购创建权限。审批内容仍保留，可稍后重试。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新优惠规则后再处理。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "合同优惠批准失败，审批内容仍保留。");
      }
      throw failure;
    }
  }
  return <>
    <ApprovalGateModal
      session={session}
      title="批准合同优惠"
      targetLabel={`${rule.tenantName} · ${rule.targetName} · ${formatMoney(rule.discountAmountMinor, rule.currency)}`}
      operation={{ action: "orders.create", targetType: "commercial_discount_rule", targetId: rule.id, tenantId: rule.tenantId, payload: discountRuleApprovalPayload(rule) }}
      impact={["会把该合同优惠标记为已批准。", "后续创建订购单时只能选择仍在有效期内且有剩余次数的规则。", "优惠金额、对象、周期和次数都以本规则回读为准。"]}
      recovery="如合同条款填错，请不要批准；已批准规则需要后续走撤销或重新创建规则处理。"
      execute={execute}
      onClose={onClose}
      onExecuted={onSaved}
    />
    {submitError && <div className="sr-only" aria-live="polite">{submitError}</div>}
  </>;
}

function DiscountRuleWizard({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [tenantId, setTenantId] = useState("");
  const [planVersionId, setPlanVersionId] = useState("");
  const [targetKind, setTargetKind] = useState<DiscountTargetKind>("plan");
  const [addonVersionId, setAddonVersionId] = useState("");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [amountText, setAmountText] = useState("");
  const [startsAt, setStartsAt] = useState(localDateTimeValue());
  const [endsAt, setEndsAt] = useState(localDateTimeOffset(30));
  const [maxRedemptions, setMaxRedemptions] = useState("1");
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `discount-rule-create-${crypto.randomUUID()}`);
  const [planDetail, setPlanDetail] = useState<PlanVersionDetail | null>(null);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [planDetailError, setPlanDetailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const loader = useCallback(async (): Promise<Omit<CreateOrderData, "discountRules">> => {
    const [tenants, plans, catalog] = await Promise.all([
      api<{ items: Tenant[] }>("/api/tenants"),
      api<{ items: PlanProductCatalogItem[] }>("/api/plan-catalog"),
      api<{ addons: AddonCatalogItem[] }>("/api/commercial/catalog"),
    ]);
    return {
      tenants: tenants.items,
      plans: plans.items.flatMap((product) => product.versions
        .filter((version) => version.status === "published")
        .map((version) => ({
          productName: product.name,
          description: product.description,
          versionId: version.id,
          version: version.version,
          changeNote: version.changeNote,
        }))),
      addons: catalog.addons.flatMap((addon) => addon.versions
        .filter((version) => version.status === "published")
        .map((version) => ({
          addonName: addon.name,
          kindLabel: addon.kindLabel,
          versionId: version.id,
          version: version.version,
          status: version.status,
          billingInterval: version.billingInterval,
          amountMinor: version.amountMinor,
          currency: version.currency,
          taxRateBp: version.taxRateBp,
          targetName: version.targetName,
          validFrom: version.validFrom,
          validTo: version.validTo,
        }))),
    };
  }, []);
  const remote = useRemote(loader, (data) => data.tenants.length === 0 || data.plans.length === 0);
  const data = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data : null;
  const selectedPrice = planDetail?.prices.find((price) => price.billingInterval === billingInterval && price.currency === "CNY") ?? planDetail?.prices.find((price) => price.currency === "CNY") ?? null;
  const planBillingOptions = (planDetail?.prices ?? [])
    .filter((price) => price.currency === "CNY")
    .map((price) => ({ value: price.billingInterval, label: `${billingIntervalLabels[price.billingInterval]} · ${formatMoney(price.amountMinor, price.currency)}` }));
  const offeredAddonIds = new Set((planDetail?.addonOffers ?? []).map((offer) => offer.addonVersionId));
  const availableAddons = (data?.addons ?? []).filter((addon) => offeredAddonIds.has(addon.versionId));
  const selectedAddon = availableAddons.find((addon) => addon.versionId === addonVersionId) ?? null;
  const targetCurrency = targetKind === "addon" ? selectedAddon?.currency : selectedPrice?.currency;
  const targetBillingInterval = targetKind === "addon" ? selectedAddon?.billingInterval : billingInterval;
  const parsedAmount = yuanToMinor(amountText);
  const parsedMaxRedemptions = /^(?:[1-9][0-9]{0,5})$/.test(maxRedemptions.trim()) ? Number.parseInt(maxRedemptions, 10) : null;
  const windowValid = startsAt && endsAt && !Number.isNaN(new Date(startsAt).getTime()) && !Number.isNaN(new Date(endsAt).getTime()) && new Date(endsAt).getTime() > new Date(startsAt).getTime();
  const canSubmit = Boolean(
    tenantId &&
    planVersionId &&
    (targetKind === "plan" || addonVersionId) &&
    targetCurrency &&
    targetBillingInterval &&
    parsedAmount &&
    parsedAmount !== "0" &&
    parsedMaxRedemptions &&
    parsedMaxRedemptions <= 100_000 &&
    windowValid &&
    reason.trim().length >= 8,
  );

  useEffect(() => {
    if (!planVersionId) {
      setPlanDetail(null);
      setPlanDetailError("");
      setPlanDetailLoading(false);
      return;
    }
    let alive = true;
    setPlanDetail(null);
    setPlanDetailError("");
    setPlanDetailLoading(true);
    void api<PlanVersionDetail>(`/api/plan-catalog/versions/${planVersionId}`)
      .then((detail) => {
        if (!alive) return;
        setPlanDetail(detail);
        const firstPrice = detail.prices.find((price) => price.currency === "CNY");
        if (firstPrice) setBillingInterval(firstPrice.billingInterval);
      })
      .catch((failure) => {
        if (!alive) return;
        setPlanDetailError(failure instanceof ApiClientError && failure.status === 403
          ? "当前账号不能读取套餐详情，请联系平台负责人开通套餐读取权限。"
          : failure instanceof Error ? failure.message : "套餐详情读取失败");
      })
      .finally(() => {
        if (alive) setPlanDetailLoading(false);
      });
    return () => { alive = false; };
  }, [planVersionId]);

  useEffect(() => {
    if (!availableAddons.some((addon) => addon.versionId === addonVersionId)) setAddonVersionId("");
  }, [addonVersionId, availableAddons]);

  async function submit() {
    if (!canSubmit || !parsedAmount || !parsedMaxRedemptions || !targetCurrency || !targetBillingInterval) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api<DiscountRuleView>("/api/discount-rules", {
        method: "POST",
        body: JSON.stringify({
          tenantId,
          planVersionId: targetKind === "plan" ? planVersionId : null,
          addonVersionId: targetKind === "addon" ? addonVersionId : null,
          billingInterval: targetBillingInterval,
          currency: targetCurrency,
          discountAmountMinor: parsedAmount,
          startsAt: fromDateTimeLocal(startsAt),
          endsAt: fromDateTimeLocal(endsAt),
          maxRedemptions: parsedMaxRedemptions,
          reason,
          idempotencyKey,
        }),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能创建合同优惠，请联系平台负责人开通订购创建权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。已填写内容仍保留，可刷新目录后重试。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "合同优惠创建失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const steps: WizardStep[] = [
    {
      key: "scope",
      title: "选择优惠范围",
      summary: "客户、套餐和加购都从真实目录选择，页面不让手填编号。",
      content: <RemotePageState
        state={remote.state}
        onRetry={remote.reload}
        blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能读取优惠所需资料", description: "请联系平台负责人开通客户、套餐与目录读取权限。" } : null}
        emptyHint="当前没有可用客户或已发布套餐，请先补齐客户和套餐目录。"
      >
        {(choices) => <div className="form-grid">
          <label><span>企业</span><V3Select value={tenantId} onChange={setTenantId} options={choices.tenants.map((tenant) => ({ value: tenant.id, label: tenant.name, hint: tenant.status === "active" ? "运行中" : "状态需确认" }))} placeholder="选择企业" ariaLabel="选择优惠企业" /></label>
          <label><span>已发布套餐</span><V3Select value={planVersionId} onChange={setPlanVersionId} options={choices.plans.map((plan) => ({ value: plan.versionId, label: `${plan.productName} · 第 ${plan.version} 版`, hint: plan.description || plan.changeNote || "已发布" }))} placeholder="选择套餐" ariaLabel="选择优惠套餐" /></label>
          <label><span>优惠对象</span><V3Select value={targetKind} onChange={(value) => setTargetKind(value as DiscountTargetKind)} options={[{ value: "plan", label: "套餐" }, { value: "addon", label: "加购项目" }]} ariaLabel="选择优惠对象" /></label>
          {targetKind === "addon" && <label><span>加购项目</span><V3Select value={addonVersionId} onChange={setAddonVersionId} options={availableAddons.map((addon) => ({ value: addon.versionId, label: `${addon.addonName} · 第 ${addon.version} 版`, hint: `${addon.kindLabel} · ${billingIntervalLabels[addon.billingInterval]} · ${formatMoney(addon.amountMinor, addon.currency)}` }))} placeholder="选择加购" disabled={availableAddons.length === 0} ariaLabel="选择优惠加购" /></label>}
          <div className="full" aria-live="polite">
            {planDetailLoading && <InlineFeedback tone="info">正在读取套餐详情和可售加购。</InlineFeedback>}
            {planDetailError && <InlineFeedback tone="danger">{planDetailError}</InlineFeedback>}
            {targetKind === "addon" && planVersionId && !planDetailLoading && availableAddons.length === 0 && <InlineFeedback tone="info">当前套餐没有可绑定的已发布加购，可先创建套餐优惠。</InlineFeedback>}
          </div>
        </div>}
      </RemotePageState>,
      validate: () => !tenantId ? "请选择企业。" : !planVersionId ? "请选择已发布套餐。" : targetKind === "addon" && !addonVersionId ? "请选择要优惠的加购项目。" : null,
    },
    {
      key: "terms",
      title: "填写优惠条款",
      summary: "金额按元填写，保存时转换为整数分；有效期和次数会一起进入审批内容。",
      content: <div className="form-grid">
        {targetKind === "plan" && <label><span>适用周期</span><V3Select value={billingInterval} onChange={(value) => setBillingInterval(value as BillingInterval)} options={planBillingOptions} placeholder="等待套餐价格" disabled={planBillingOptions.length === 0} ariaLabel="选择优惠周期" /></label>}
        {targetKind === "addon" && <label><span>适用周期</span><input className="input" value={selectedAddon ? billingIntervalLabels[selectedAddon.billingInterval] : "等待选择加购"} disabled /></label>}
        <label><span>优惠金额（元）</span><input className="input" inputMode="decimal" value={amountText} onChange={(event) => setAmountText(event.target.value)} placeholder="例如 100.00" /></label>
        <label><span>开始时间</span><input className="input" type="datetime-local" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} /></label>
        <label><span>结束时间</span><input className="input" type="datetime-local" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} /></label>
        <label><span>最多使用次数</span><input className="input" inputMode="numeric" value={maxRedemptions} onChange={(event) => setMaxRedemptions(event.target.value)} /></label>
        <div className="full" aria-live="polite">
          {amountText && (!parsedAmount || parsedAmount === "0") && <InlineFeedback tone="danger">请填写大于 0 的优惠金额，最多保留 2 位小数。</InlineFeedback>}
          {!windowValid && <InlineFeedback tone="danger">优惠结束时间必须晚于开始时间。</InlineFeedback>}
          {maxRedemptions && (!parsedMaxRedemptions || parsedMaxRedemptions > 100_000) && <InlineFeedback tone="danger">最多使用次数请填写 1 到 100000 之间的整数。</InlineFeedback>}
        </div>
      </div>,
      validate: () => targetKind === "plan" && !selectedPrice ? "所选套餐缺少当前币种的价格，请先补齐套餐价格。" : !parsedAmount || parsedAmount === "0" ? "请填写大于 0 的优惠金额，最多保留 2 位小数。" : !windowValid ? "优惠结束时间必须晚于开始时间。" : !parsedMaxRedemptions || parsedMaxRedemptions > 100_000 ? "最多使用次数请填写 1 到 100000 之间的整数。" : null,
    },
    {
      key: "reason",
      title: "确认依据",
      summary: "保存后规则处于待审批，批准后才会出现在订购向导里。",
      content: <div className="form-grid">
        <div className="full"><InlineFeedback tone="info">提交后只会创建待审批规则，不会直接给订单打折。需要另一名管理员批准后，订购单才能选择使用。</InlineFeedback></div>
        <label className="full"><span>优惠依据</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明合同条款或审批依据，至少 8 个字。" /></label>
      </div>,
      validate: () => reason.trim().length < 8 ? "请填写至少 8 个字的优惠依据。" : null,
    },
  ];

  return <Wizard
    title="新建合同优惠"
    description="创建待审批优惠规则，批准后才能用于订购单。"
    steps={steps}
    submitLabel="创建优惠规则"
    onSubmit={() => void submit()}
    onClose={onClose}
    submitting={submitting}
    submitError={submitError}
    submitDisabled={!canSubmit || remote.state.status !== "ready"}
  />;
}

function CreateOrderWizard({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [tenantId, setTenantId] = useState("");
  const [planVersionId, setPlanVersionId] = useState("");
  const [billingInterval, setBillingInterval] = useState<BillingInterval>("monthly");
  const [channel, setChannel] = useState<PurchaseChannel>("platform_sales");
  const [planQuantity, setPlanQuantity] = useState("1");
  const [addons, setAddons] = useState<AddonDraft[]>([]);
  const [selectedDiscountRuleId, setSelectedDiscountRuleId] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `order-create-${crypto.randomUUID()}`);
  const [planDetail, setPlanDetail] = useState<PlanVersionDetail | null>(null);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);
  const [planDetailError, setPlanDetailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const loader = useCallback(async (): Promise<CreateOrderData> => {
    const [tenants, plans, catalog, discountRules] = await Promise.all([
      api<{ items: Tenant[] }>("/api/tenants"),
      api<{ items: PlanProductCatalogItem[] }>("/api/plan-catalog"),
      api<{ addons: AddonCatalogItem[] }>("/api/commercial/catalog"),
      api<{ items: DiscountRuleView[] }>("/api/discount-rules"),
    ]);
    return {
      tenants: tenants.items,
      plans: plans.items.flatMap((product) => product.versions
        .filter((version) => version.status === "published")
        .map((version) => ({
          productName: product.name,
          description: product.description,
          versionId: version.id,
          version: version.version,
          changeNote: version.changeNote,
        }))),
      addons: catalog.addons.flatMap((addon) => addon.versions
        .filter((version) => version.status === "published")
        .map((version) => ({
          addonName: addon.name,
          kindLabel: addon.kindLabel,
          versionId: version.id,
          version: version.version,
          status: version.status,
          billingInterval: version.billingInterval,
          amountMinor: version.amountMinor,
          currency: version.currency,
          taxRateBp: version.taxRateBp,
          targetName: version.targetName,
          validFrom: version.validFrom,
          validTo: version.validTo,
        }))),
      discountRules: discountRules.items,
    };
  }, []);
  const remote = useRemote(loader, (data) => data.tenants.length === 0 || data.plans.length === 0);
  const choices = remote.state.status === "ready" || remote.state.status === "empty" ? remote.state.data : null;
  const selectedTenant = choices?.tenants.find((tenant) => tenant.id === tenantId);
  const selectedPlan = choices?.plans.find((plan) => plan.versionId === planVersionId);
  const validPrices = useMemo(() => {
    const now = Date.now();
    return (planDetail?.prices ?? []).filter((price) =>
      price.currency === "CNY" &&
      new Date(price.validFrom).getTime() <= now &&
      (!price.validTo || new Date(price.validTo).getTime() > now));
  }, [planDetail]);
  const validBillingOptions = useMemo(() => validPrices.map((price) => ({
    value: price.billingInterval,
    label: `${billingIntervalLabels[price.billingInterval]} · ${formatMoney(price.amountMinor, price.currency)} · ${price.taxMode === "tax_inclusive" ? "含税" : "未税"} · 税率 ${price.taxRateBp / 100}%`,
  })), [validPrices]);
  const selectedPrice = validPrices.find((price) => price.billingInterval === billingInterval) ?? validPrices[0] ?? null;
  const priceBlockMessage = planVersionId && !planDetailLoading && !selectedPrice ? "所选套餐缺少可用价格/税务配置，请先在套餐目录补齐后再创建订购单。" : "";
  const availableAddons = useMemo(() => {
    if (!choices || !planDetail || !selectedPrice) return [];
    const now = Date.now();
    const offers = new Map(planDetail.addonOffers.map((offer) => [offer.addonVersionId, offer]));
    return choices.addons.filter((addon) => {
      const offer = offers.get(addon.versionId);
      if (!offer) return false;
      if (addon.status !== "published" || offer.status !== "published") return false;
      if (addon.billingInterval !== selectedPrice.billingInterval || offer.billingInterval !== selectedPrice.billingInterval) return false;
      if (addon.currency !== selectedPrice.currency || offer.currency !== selectedPrice.currency) return false;
      return new Date(addon.validFrom).getTime() <= now && (!addon.validTo || new Date(addon.validTo).getTime() > now);
    });
  }, [choices, planDetail, selectedPrice]);
  const availableAddonIds = useMemo(() => new Set(availableAddons.map((addon) => addon.versionId)), [availableAddons]);
  const addonRows = addons
    .map((draft) => ({ draft, addon: availableAddons.find((item) => item.versionId === draft.versionId) ?? null }))
    .filter((item): item is { draft: AddonDraft; addon: NonNullable<typeof item.addon> } => Boolean(item.addon));
  const selectedAddonIds = useMemo(() => new Set(addonRows.map(({ draft }) => draft.versionId)), [addonRows]);
  const availableDiscountRules = useMemo(() => {
    if (!choices || !selectedPrice || !tenantId || !planVersionId) return [];
    const now = Date.now();
    return choices.discountRules.filter((rule) => {
      if (rule.status !== "approved" || !rule.approvalRequestId) return false;
      if (rule.tenantId !== tenantId || rule.currency !== selectedPrice.currency) return false;
      if (rule.billingInterval !== null && rule.billingInterval !== billingInterval) return false;
      if (new Date(rule.startsAt).getTime() > now || new Date(rule.endsAt).getTime() <= now) return false;
      if (rule.redeemedCount >= rule.maxRedemptions) return false;
      if (rule.planVersionId) return rule.planVersionId === planVersionId;
      if (rule.addonVersionId) return selectedAddonIds.has(rule.addonVersionId);
      return false;
    });
  }, [billingInterval, choices, planVersionId, selectedAddonIds, selectedPrice, tenantId]);
  const selectedDiscountRule = availableDiscountRules.find((rule) => rule.id === selectedDiscountRuleId) ?? null;
  const canSubmit = Boolean(
    tenantId &&
    planVersionId &&
    selectedPrice &&
    isPositiveQuantity(planQuantity) &&
    addons.every((item) => item.versionId && availableAddonIds.has(item.versionId) && isPositiveQuantity(item.quantity)) &&
    reason.trim().length >= 8,
  );

  useEffect(() => {
    if (!planVersionId) {
      setPlanDetail(null);
      setPlanDetailError("");
      setPlanDetailLoading(false);
      return;
    }
    let alive = true;
    setPlanDetail(null);
    setPlanDetailError("");
    setPlanDetailLoading(true);
    void api<PlanVersionDetail>(`/api/plan-catalog/versions/${planVersionId}`)
      .then((detail) => {
        if (!alive) return;
        setPlanDetail(detail);
        const now = Date.now();
        const firstPrice = detail.prices.find((price) =>
          price.currency === "CNY" &&
          new Date(price.validFrom).getTime() <= now &&
          (!price.validTo || new Date(price.validTo).getTime() > now));
        if (firstPrice) setBillingInterval(firstPrice.billingInterval);
      })
      .catch((failure) => {
        if (!alive) return;
        setPlanDetailError(failure instanceof ApiClientError && failure.status === 403
          ? "当前账号不能读取套餐价格与税务配置，请联系平台负责人开通套餐读取权限。"
          : failure instanceof Error ? failure.message : "套餐价格读取失败");
      })
      .finally(() => {
        if (alive) setPlanDetailLoading(false);
      });
    return () => { alive = false; };
  }, [planVersionId]);

  useEffect(() => {
    setAddons((current) => current.filter((item) => !item.versionId || availableAddonIds.has(item.versionId)));
  }, [availableAddonIds]);

  useEffect(() => {
    if (selectedDiscountRuleId && !availableDiscountRules.some((rule) => rule.id === selectedDiscountRuleId)) {
      setSelectedDiscountRuleId("");
    }
  }, [availableDiscountRules, selectedDiscountRuleId]);

  function updateAddon(index: number, patch: Partial<AddonDraft>) {
    setAddons((current) => current.map((item, i) => i === index ? { ...item, ...patch } : item));
  }

  async function submit() {
    if (!canSubmit || !selectedPrice) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await api<PurchaseOrderDetail>("/api/orders", {
        method: "POST",
        body: JSON.stringify({
          tenantId,
          subscriptionId: null,
          channel,
          currency: "CNY",
          billingInterval,
          items: [
            { itemType: "plan", planVersionId, addonVersionId: null, quantity: Number.parseInt(planQuantity, 10) },
            ...addonRows.map(({ draft }) => ({ itemType: "addon", planVersionId: null, addonVersionId: draft.versionId, quantity: Number.parseInt(draft.quantity, 10) })),
          ],
          discount: selectedDiscountRule
            ? { amountMinor: selectedDiscountRule.discountAmountMinor, ruleId: selectedDiscountRule.id, approvalId: selectedDiscountRule.approvalRequestId, reason: selectedDiscountRule.reason }
            : { amountMinor: "0", ruleId: null, approvalId: null, reason: "" },
          taxMode: selectedPrice.taxMode,
          taxRateBp: selectedPrice.taxRateBp,
          notes,
          reason,
          idempotencyKey,
        }),
      });
      onSaved();
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能新建订购单，请联系平台负责人开通订购创建权限。已填写内容仍保留在表单中。");
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "订购单创建失败，已填写内容仍保留。");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const steps: WizardStep[] = [
    {
      key: "customer",
      title: "选择客户",
      summary: "客户和可售目录来自真实控制面接口，页面只显示业务名称。",
      content: <RemotePageState
        state={remote.state}
        onRetry={remote.reload}
        blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能读取订购所需资料", description: "请联系平台负责人开通客户、套餐与目录读取权限；页面不会用手工输入替代真实客户。" } : null}
        emptyHint="当前没有可订购客户或已发布套餐，请先确认客户状态和套餐发布状态。"
      >
        {(data) => <div className="form-grid">
          <label><span>客户</span><V3Select value={tenantId} onChange={setTenantId} options={data.tenants.map((tenant) => ({ value: tenant.id, label: tenant.name, hint: tenant.status === "active" ? "运行中" : "状态需确认" }))} placeholder="选择客户" ariaLabel="选择客户" /></label>
          <label><span>已发布套餐</span><V3Select value={planVersionId} onChange={setPlanVersionId} options={data.plans.map((plan) => ({ value: plan.versionId, label: `${plan.productName} · 第 ${plan.version} 版`, hint: plan.description || plan.changeNote || "已发布" }))} placeholder="选择套餐" ariaLabel="选择已发布套餐" /></label>
        </div>}
      </RemotePageState>,
      validate: () => !tenantId ? "请选择客户。" : !planVersionId ? "请选择已发布套餐。" : null,
    },
    {
      key: "items",
      title: "配置订购项",
      summary: "这里只选择周期、渠道和数量；最终应收金额由系统在创建订购单时计算。",
      content: <div className="form-grid">
        <label><span>订购周期</span><V3Select value={selectedPrice?.billingInterval ?? billingInterval} onChange={(value) => setBillingInterval(value as BillingInterval)} options={validBillingOptions} placeholder="等待价格配置" disabled={validBillingOptions.length === 0} ariaLabel="选择订购周期" /></label>
        <label><span>购买渠道</span><V3Select value={channel} onChange={(value) => setChannel(value as PurchaseChannel)} options={channelOptions} ariaLabel="选择购买渠道" /></label>
        <label><span>套餐数量</span><input className="input" inputMode="numeric" value={planQuantity} onChange={(event) => setPlanQuantity(event.target.value)} /></label>
        <div className="full" aria-live="polite">
          {planDetailLoading && <InlineFeedback tone="info">正在读取套餐价格与税务配置。</InlineFeedback>}
          {planDetailError && <InlineFeedback tone="danger">{planDetailError}</InlineFeedback>}
          {priceBlockMessage && <InlineFeedback tone="danger">{priceBlockMessage}</InlineFeedback>}
          {selectedPrice && <InlineFeedback tone="info">当前周期来自已发布价格：{formatMoney(selectedPrice.amountMinor, selectedPrice.currency)}，{selectedPrice.taxMode === "tax_inclusive" ? "含税" : "未税"}，税率 {selectedPrice.taxRateBp / 100}%。最终应收以创建后的订购单为准。</InlineFeedback>}
        </div>
        <div className="full order-addon-editor">
          <div className="order-addon-editor-head">
            <strong>加购项目</strong>
            <button type="button" className="button secondary" onClick={() => setAddons((current) => [...current, { versionId: "", quantity: "1" }])}>添加加购</button>
          </div>
          {addons.map((item, index) => <div className="order-addon-row" key={index}>
            <V3Select value={item.versionId} onChange={(value) => updateAddon(index, { versionId: value })} options={availableAddons.map((addon) => ({ value: addon.versionId, label: `${addon.addonName} · 第 ${addon.version} 版`, hint: `${addon.kindLabel} · ${billingIntervalLabels[addon.billingInterval]} · ${formatMoney(addon.amountMinor, addon.currency)} · 税率 ${addon.taxRateBp / 100}%` }))} placeholder="选择加购" disabled={availableAddons.length === 0} ariaLabel="选择加购项目" />
            <label><span>数量</span><input className="input" inputMode="numeric" value={item.quantity} onChange={(event) => updateAddon(index, { quantity: event.target.value })} /></label>
            <button type="button" className="button ghost" onClick={() => setAddons((current) => current.filter((_, i) => i !== index))}>移除</button>
          </div>)}
          {availableAddons.length === 0 && <span className="empty-inline">当前套餐和周期没有可用加购，可直接创建无加购订购单。</span>}
          {availableAddons.length > 0 && addons.length === 0 && <span className="empty-inline">可不选择加购项目。</span>}
        </div>
      </div>,
      validate: () => !selectedPrice ? "所选套餐缺少可用价格/税务配置。" : !isPositiveQuantity(planQuantity) ? "请输入有效套餐数量。" : addons.some((item) => !item.versionId || !availableAddonIds.has(item.versionId) || !isPositiveQuantity(item.quantity)) ? "请补齐有效加购项目和数量，或移除该行。" : null,
    },
    {
      key: "billing",
      title: "业务备注",
      summary: "可选择已批准且当前可用的合同优惠；税务口径来自已发布价格，创建时会再次校验。",
      content: <div className="form-grid">
        <label className="full"><span>合同优惠</span><V3Select value={selectedDiscountRuleId} onChange={setSelectedDiscountRuleId} options={[{ value: "", label: "不使用合同优惠" }, ...availableDiscountRules.map((rule) => ({ value: rule.id, label: `${ruleTargetLabel(rule)} · ${formatMoney(rule.discountAmountMinor, rule.currency)}`, hint: `${rule.targetName} · ${formatDate(rule.startsAt)} 至 ${formatDate(rule.endsAt)} · 剩余 ${rule.maxRedemptions - rule.redeemedCount} 次` }))]} ariaLabel="选择合同优惠" /></label>
        <div className="full"><InlineFeedback tone="info">{availableDiscountRules.length > 0 ? "这里只能选择已批准、未过期、同企业、同套餐、同周期、同币种且有剩余次数的规则；优惠金额由规则自动带入。" : "当前没有可用于本次订购的合同优惠，将按无优惠创建。"}</InlineFeedback></div>
        <label className="full"><span>业务备注</span><textarea className="input" rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="可填写合同或客户沟通信息" /></label>
      </div>,
    },
    {
      key: "confirm",
      title: "提交创建",
      summary: selectedTenant && selectedPlan ? `${selectedTenant.name} · ${selectedPlan.productName} · ${billingIntervalLabels[billingInterval]}` : "填写创建原因后提交。",
      content: <div className="form-grid">
        <label className="full"><span>创建原因</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明创建订购单的业务依据，至少 8 个字。" /></label>
      </div>,
      validate: () => reason.trim().length < 8 ? "请填写至少 8 个字的创建原因。" : null,
    },
  ];

  return <Wizard
    title="新建订购"
    description="创建后进入线下确认收款流程，列表刷新后展示最新计算结果。"
    steps={steps}
    submitLabel="创建订购单"
    onSubmit={() => void submit()}
    onClose={onClose}
    submitting={submitting}
    submitError={submitError}
    submitDisabled={!canSubmit || remote.state.status !== "ready"}
  />;
}

function OfflineConfirmFlow({ session, order, onClose, onSaved }: { session: SessionView; order: PurchaseOrderLedgerItem; onClose: () => void; onSaved: () => void }) {
  const [confirmationReference, setConfirmationReference] = useState("");
  const [evidence, setEvidence] = useState("");
  const [paidAt, setPaidAt] = useState(localDateTimeValue());
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `offline-confirm-${crypto.randomUUID()}`);
  const [gateOpen, setGateOpen] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitError, setSubmitError] = useState("");

  function validate() {
    if (confirmationReference.trim().length < 4) return "请填写至少 4 个字符的业务凭证号。";
    if (evidence.trim().length < 8) return "请填写至少 8 个字的业务证据或备注。";
    if (!paidAt || Number.isNaN(new Date(paidAt).getTime())) return "请选择实际到账时间。";
    if (reason.trim().length < 8) return "请填写至少 8 个字的确认原因。";
    return null;
  }

  function openGate() {
    const failure = validate();
    setFormError(failure ?? "");
    setSubmitError("");
    if (!failure) setGateOpen(true);
  }

  async function execute(approvalId: string) {
    setSubmitError("");
    try {
      await api<PurchaseOrderDetail>(`/api/orders/${order.id}/offline-confirm`, {
        method: "POST",
        body: JSON.stringify({
          confirmationReference,
          evidence,
          paidAt: fromDateTimeLocal(paidAt),
          approvalId,
          reason,
          idempotencyKey,
        }),
      });
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能确认线下收款，请联系平台负责人开通线下确认收款权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新订购单状态后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "线下确认收款失败，已填写内容仍保留。");
      }
      throw failure;
    }
  }

  return <>
    <Drawer title="线下确认收款" description={`${order.tenantName} · ${order.orderNo} · ${formatMoney(order.totalMinor, order.currency)}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" onClick={openGate}><ShieldAlert size={15} />创建或选择审批</button></div>}>
      <div className="form-grid offline-confirm-form">
        <div className="full"><InlineFeedback tone="info">当前为线下确认模式：先提交双人审批，批准后才会记录到账并开通服务；页面不会显示在线支付动画。</InlineFeedback></div>
        <label><span>业务凭证号</span><input className="input" value={confirmationReference} onChange={(event) => setConfirmationReference(event.target.value)} placeholder="银行回单号或收款流水号" /></label>
        <label><span>实际到账时间</span><input className="input" type="datetime-local" value={paidAt} onChange={(event) => setPaidAt(event.target.value)} /></label>
        <label className="full"><span>业务证据或备注</span><textarea className="input" rows={3} value={evidence} onChange={(event) => setEvidence(event.target.value)} placeholder="说明回单、账户或对账依据，至少 8 个字。" /></label>
        <label className="full"><span>确认原因</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明为何确认该笔收款，至少 8 个字。" /></label>
      </div>
      <div aria-live="polite">
        {formError && <InlineFeedback tone="danger">{formError}</InlineFeedback>}
        {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
      </div>
    </Drawer>
    {gateOpen && <ApprovalGateModal
      session={session}
      title="线下确认收款"
      targetLabel={`${order.tenantName} · ${order.orderNo}`}
      operation={{ action: "payments.confirm_offline", targetType: "purchase_order", targetId: order.id, tenantId: order.tenantId, payload: { orderId: order.id, amountMinor: order.totalMinor, confirmationReference } }}
      impact={["会记录线下收款凭证，并生成收款记录。", "会出具已收款账单，并开通对应订阅。", "结果以订购与账单台账回读为准。"]}
      recovery="如凭证填写错误，请在账单或退款流程中追加处理；本页不会触发在线支付流程。"
      execute={(approvalId) => execute(approvalId)}
      onClose={() => setGateOpen(false)}
      onExecuted={() => { setGateOpen(false); onSaved(); }}
    />}
  </>;
}

function InvoiceAdjustFlow({ session, invoice, tenantId, onClose, onSaved }: { session: SessionView; invoice: InvoiceView; tenantId: string; onClose: () => void; onSaved: () => void }) {
  const [lineType, setLineType] = useState<"adjustment" | "discount">("adjustment");
  const [amountText, setAmountText] = useState("");
  const [description, setDescription] = useState("");
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `invoice-adjust-${crypto.randomUUID()}`);
  const [gateOpen, setGateOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const amountMinor = signedYuanToMinor(amountText);
  const canSubmit = Boolean(amountMinor && amountMinor !== "0" && description.trim().length >= 2 && reason.trim().length >= 8);

  async function execute(approvalId: string) {
    if (!amountMinor) return;
    setSubmitError("");
    try {
      await api<InvoiceView>(`/api/invoices/${invoice.id}/adjust`, {
        method: "POST",
        body: JSON.stringify({ lineType, description, amountMinor, reason, approvalId, idempotencyKey }),
      });
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能调整账单，请联系平台负责人开通账单调整权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新账单状态后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "账单调整失败，已填写内容仍保留。");
      }
      throw failure;
    }
  }

  return <>
    <Drawer title="调整账单" description={`${invoice.tenantName} · ${invoice.invoiceNo} · ${formatMoney(invoice.totalMinor, invoice.currency)}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!canSubmit} onClick={() => setGateOpen(true)}>创建或选择审批</button></div>}>
      <div className="form-grid">
        <div className="full"><InlineFeedback tone="info">账单调整只会追加调整明细，不会覆盖已出账事实。增加金额填正数，减免金额填负数。</InlineFeedback></div>
        <label><span>调整类型</span><V3Select value={lineType} onChange={(value) => setLineType(value as "adjustment" | "discount")} options={[{ value: "adjustment", label: "业务调整" }, { value: "discount", label: "账单减免" }]} ariaLabel="选择账单调整类型" /></label>
        <label><span>调整金额（元）</span><input className="input" inputMode="decimal" value={amountText} onChange={(event) => setAmountText(event.target.value)} placeholder="例如 -20.00 或 15.00" /></label>
        <label className="full"><span>账单明细说明</span><input className="input" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="例如 合同减免或人工补差" /></label>
        <label className="full"><span>调整理由</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明调整依据，至少 8 个字。" /></label>
      </div>
      <div aria-live="polite">
        {amountText && (!amountMinor || amountMinor === "0") && <InlineFeedback tone="danger">请填写非 0 调整金额，最多保留 2 位小数；减免可填写负数。</InlineFeedback>}
        {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
      </div>
    </Drawer>
    {gateOpen && amountMinor && <ApprovalGateModal
      session={session}
      title="审批账单调整"
      targetLabel={`${invoice.tenantName} · ${invoice.invoiceNo} · ${formatMoney(amountMinor, invoice.currency)}`}
      operation={{ action: "invoices.adjust", targetType: "invoice", targetId: invoice.id, tenantId, payload: { invoiceId: invoice.id, lineType, amountMinor } }}
      impact={["会在账单上追加一条调整明细。", "不会覆盖原始出账金额和历史事实。", "执行结果以账单回读为准。"]}
      recovery="如金额或类型填错，请不要执行；需要更正时重新追加一条相反方向的调整。"
      execute={(approvalId) => execute(approvalId)}
      onClose={() => setGateOpen(false)}
      onExecuted={() => { setGateOpen(false); onSaved(); }}
    />}
  </>;
}

function RefundDecisionFlow({ session, refund, tenantId, onClose, onSaved }: { session: SessionView; refund: RefundTransactionView; tenantId: string; onClose: () => void; onSaved: () => void }) {
  const [decision, setDecision] = useState<"approved" | "rejected">("approved");
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `refund-decision-${crypto.randomUUID()}`);
  const [gateOpen, setGateOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const canSubmit = reason.trim().length >= 8;

  async function execute(approvalId: string) {
    setSubmitError("");
    try {
      await api<PurchaseOrderDetail>(`/api/refunds/${refund.id}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision, reason, approvalId, idempotencyKey }),
      });
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能审核退款，请联系平台负责人开通退款审核权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新退款状态后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "退款审核失败，已填写内容仍保留。");
      }
      throw failure;
    }
  }

  return <>
    <Drawer title="审核退款" description={`${refund.tenantName} · ${formatMoney(refund.amountMinor, refund.currency)} · ${refundHandlingLabels[refund.subscriptionHandling]}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!canSubmit} onClick={() => setGateOpen(true)}>创建或选择审批</button></div>}>
      <div className="form-grid">
        <div className="full"><InlineFeedback tone="info">审核只改变退款申请状态；批准后仍需执行退款，页面不会提前显示已退款。</InlineFeedback></div>
        <label><span>审核结论</span><V3Select value={decision} onChange={(value) => setDecision(value as "approved" | "rejected")} options={[{ value: "approved", label: "同意退款" }, { value: "rejected", label: "拒绝退款" }]} ariaLabel="选择退款审核结论" /></label>
        <label className="full"><span>审核理由</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明审核依据，至少 8 个字。" /></label>
      </div>
      <div aria-live="polite">
        {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
      </div>
    </Drawer>
    {gateOpen && <ApprovalGateModal
      session={session}
      title="审批退款审核"
      targetLabel={`${refund.tenantName} · ${formatMoney(refund.amountMinor, refund.currency)}`}
      operation={{ action: "refunds.approve", targetType: "refund_transaction", targetId: refund.id, tenantId, payload: { refundId: refund.id, decision, amountMinor: refund.amountMinor, subscriptionHandling: refund.subscriptionHandling } }}
      impact={["会记录退款审核结论。", "同意退款后仍需执行退款，不会直接显示退款成功。", "退款金额和订阅影响以正式退款记录为准。"]}
      recovery="如结论或理由填错，请不要执行；退款状态变更后需按真实业务重新发起后续处理。"
      execute={(approvalId) => execute(approvalId)}
      onClose={() => setGateOpen(false)}
      onExecuted={() => { setGateOpen(false); onSaved(); }}
    />}
  </>;
}

function RefundExecuteFlow({ session, refund, tenantId, onClose, onSaved }: { session: SessionView; refund: RefundTransactionView; tenantId: string; onClose: () => void; onSaved: () => void }) {
  const [result, setResult] = useState<"succeeded" | "failed">("succeeded");
  const [executionReference, setExecutionReference] = useState("");
  const [executionEvidence, setExecutionEvidence] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const [reason, setReason] = useState("");
  const [idempotencyKey] = useState(() => `refund-execute-${crypto.randomUUID()}`);
  const [gateOpen, setGateOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const successValid = result === "succeeded" ? executionReference.trim().length >= 4 && executionEvidence.trim().length >= 8 : true;
  const failedValid = result === "failed" ? failureMessage.trim().length >= 8 : true;
  const canSubmit = reason.trim().length >= 8 && successValid && failedValid;

  async function execute(approvalId: string) {
    setSubmitError("");
    try {
      await api<PurchaseOrderDetail>(`/api/refunds/${refund.id}/execute`, {
        method: "POST",
        body: JSON.stringify({
          result,
          executionReference: result === "succeeded" ? executionReference : null,
          executionEvidence: result === "succeeded" ? executionEvidence : null,
          failureMessage: result === "failed" ? failureMessage : null,
          reason,
          approvalId,
          idempotencyKey,
        }),
      });
    } catch (failure) {
      if (failure instanceof ApiClientError && failure.status === 403) {
        setSubmitError("当前账号不能执行退款，请联系平台负责人开通退款执行权限。已填写内容仍保留。");
      } else if (failure instanceof ApiClientError && failure.status === 409) {
        setSubmitError(`${failure.message}。请刷新退款状态后再处理，已填写内容仍保留。`);
      } else {
        setSubmitError(failure instanceof Error ? failure.message : "退款执行失败，已填写内容仍保留。");
      }
      throw failure;
    }
  }

  return <>
    <Drawer title="执行退款" description={`${refund.tenantName} · ${formatMoney(refund.amountMinor, refund.currency)} · ${refundHandlingLabels[refund.subscriptionHandling]}`} onClose={onClose} footer={<div className="inline-actions"><button className="button secondary" onClick={onClose}>取消</button><button className="button primary" disabled={!canSubmit} onClick={() => setGateOpen(true)}>创建或选择审批</button></div>}>
      <div className="form-grid">
        <div className="full"><InlineFeedback tone="info">执行结果必须来自真实退款处理证据；待处理不会被显示成已退款。</InlineFeedback></div>
        <label><span>执行结果</span><V3Select value={result} onChange={(value) => setResult(value as "succeeded" | "failed")} options={[{ value: "succeeded", label: "退款成功" }, { value: "failed", label: "退款失败" }]} ariaLabel="选择退款执行结果" /></label>
        {result === "succeeded" && <>
          <label><span>退款回单号</span><input className="input" value={executionReference} onChange={(event) => setExecutionReference(event.target.value)} placeholder="填写支付渠道回单号" /></label>
          <label className="full"><span>退款证据</span><textarea className="input" rows={3} value={executionEvidence} onChange={(event) => setExecutionEvidence(event.target.value)} placeholder="说明支付渠道、回单或银行流水证据，至少 8 个字。" /></label>
        </>}
        {result === "failed" && <label className="full"><span>失败原因</span><textarea className="input" rows={3} value={failureMessage} onChange={(event) => setFailureMessage(event.target.value)} placeholder="说明失败原因，至少 8 个字。" /></label>}
        <label className="full"><span>执行说明</span><textarea className="input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="说明本次执行依据，至少 8 个字。" /></label>
      </div>
      <div aria-live="polite">
        {submitError && <InlineFeedback tone="danger">{submitError}</InlineFeedback>}
      </div>
    </Drawer>
    {gateOpen && <ApprovalGateModal
      session={session}
      title="审批退款执行"
      targetLabel={`${refund.tenantName} · ${formatMoney(refund.amountMinor, refund.currency)}`}
      operation={{ action: "refunds.execute", targetType: "refund_transaction", targetId: refund.id, tenantId, payload: { refundId: refund.id, amountMinor: refund.amountMinor, subscriptionHandling: refund.subscriptionHandling } }}
      impact={["会记录真实退款执行结果和证据。", "退款成功才会进入已退款事实；失败只记录失败原因。", "退款金额和订阅影响以正式退款记录为准。"]}
      recovery="如回单或证据填错，请不要执行；已执行结果只能通过后续真实业务记录修正。"
      execute={(approvalId) => execute(approvalId)}
      onClose={() => setGateOpen(false)}
      onExecuted={() => { setGateOpen(false); onSaved(); }}
    />}
  </>;
}

function OrderDetailDrawer({ orderId, onClose, session, onChanged }: { orderId: string; onClose: () => void; session?: SessionView | undefined; onChanged?: (() => void) | undefined }) {
  const [refundPayment, setRefundPayment] = useState<PaymentTransactionView | null>(null);
  const [invoiceIssue, setInvoiceIssue] = useState<InvoiceView | null>(null);
  const [invoiceAdjust, setInvoiceAdjust] = useState<InvoiceView | null>(null);
  const [refundDecision, setRefundDecision] = useState<RefundTransactionView | null>(null);
  const [refundExecute, setRefundExecute] = useState<RefundTransactionView | null>(null);
  const loader = useCallback(() => api<PurchaseOrderDetail>(`/api/orders/${orderId}`), [orderId]);
  const remote = useRemote(loader);
  const canIssueInvoice = hasAction(session, "invoices.issue");
  const canAdjustInvoice = hasAction(session, "invoices.adjust");
  const canRequestRefund = hasAction(session, "refunds.request");
  const canApproveRefund = hasAction(session, "refunds.approve");
  const canExecuteRefund = hasAction(session, "refunds.execute");

  const reloadDetail = () => {
    if (onChanged) {
      onChanged();
    } else {
      void remote.reload();
    }
  };

  return <Drawer title="订购单详情" description="查看订单、账单、收款和退款证据。" onClose={onClose} footer={<button className="button secondary" onClick={onClose}>关闭</button>}>
    <RemotePageState
      state={remote.state}
      onRetry={remote.reload}
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403 ? { title: "当前账号不能查看订购详情", description: "请联系平台负责人开通订购读取权限；详情不会用空数据冒充。" } : null}
      emptyHint="这条订购单暂时没有可展示的明细。"
    >
      {(data) => <div className="order-detail">
        <section className="detail-hero">
          <div><strong>{data.orderNo}</strong><span>{data.tenantName} · {data.channelLabel}</span></div>
          <StatusBadge value={data.status} />
        </section>
        <div className="detail-grid">
          <span><strong>订单状态</strong>{data.statusLabel}</span>
          <span><strong>订单金额</strong><span className="app-number">{formatMoney(data.totalMinor, data.currency)}</span></span>
          <span><strong>商品小计</strong><span className="app-number">{formatMoney(data.subtotalMinor, data.currency)}</span></span>
          <span><strong>优惠金额</strong><span className="app-number">{formatMoney(data.discountMinor, data.currency)}</span></span>
          <span><strong>税费</strong><span className="app-number">{formatMoney(data.taxMinor, data.currency)}</span></span>
          <span><strong>收款凭证</strong>{data.confirmationReference ?? "待确认"}</span>
          <span><strong>收款时间</strong>{formatDateTime(data.confirmedAt)}</span>
          <span><strong>下一步</strong>{data.nextAction}</span>
        </div>
        {data.notes && <div className="order-note"><strong>备注</strong><span>{data.notes}</span></div>}

        <SectionCard icon={Building2} title="开票资料快照" className="drawer-section">
          <BillingProfileEvidence profile={data.billingProfileSnapshot} />
        </SectionCard>

        <SectionCard icon={ReceiptText} title="套餐与加购" className="drawer-section">
          <DataTable
            columns={[
              { key: "name", header: "项目", render: (item) => <span>{item.displayName}</span> },
              { key: "quantity", header: "数量", render: (item) => <span className="app-number">{item.quantity}</span> },
              { key: "unit", header: "单价", render: (item) => <span className="app-number">{formatMoney(item.unitAmountMinor, data.currency)}</span> },
              { key: "amount", header: "金额", render: (item) => <span className="app-number">{formatMoney(item.netMinor, data.currency)}</span> },
            ]}
            rows={data.items}
            rowKey={(item) => item.id}
            ariaLabel="订购项目"
            empty={<span>暂无项目明细。</span>}
          />
        </SectionCard>

        <SectionCard icon={FileCheck2} title="账单" className="drawer-section">
          <DataTable
            columns={[
              { key: "invoice", header: "账单", render: (item) => <span>{item.invoiceNo}</span> },
              { key: "status", header: "状态", render: (item) => <><StatusBadge value={item.status} /><small className="block">{item.statusLabel}</small></> },
              { key: "period", header: "账期", render: (item) => <span>{formatDate(item.periodStart)} 至 {formatDate(item.periodEnd)}</span> },
              { key: "amount", header: "金额", render: (item) => <span className="app-number">{formatMoney(item.totalMinor, item.currency)}</span> },
              { key: "profile", header: "开票快照", render: (item) => <span>{billingConfiguredLabel(item.billingProfileSnapshot)}</span> },
            ]}
            rows={data.invoices}
            rowKey={(item) => item.id}
            ariaLabel="订购单账单"
            empty={<span>暂无账单。</span>}
            rowActions={(item) => <div className="row-actions">
              {canIssueInvoice && item.status === "draft" && <button className="button secondary" onClick={() => setInvoiceIssue(item)}>签发账单</button>}
              {canAdjustInvoice && (item.status === "open" || item.status === "uncollectible") && session && <button className="button secondary" onClick={() => setInvoiceAdjust(item)}>调整账单</button>}
              {!(canIssueInvoice && item.status === "draft") && !(canAdjustInvoice && (item.status === "open" || item.status === "uncollectible")) && <span className="muted-action">查看</span>}
            </div>}
          />
        </SectionCard>

        <SectionCard icon={CreditCard} title="支付证据" className="drawer-section">
          <DataTable
            columns={[
              { key: "method", header: "收款方式", render: (item) => <span>{item.methodLabel}</span> },
              { key: "status", header: "到账状态", render: (item) => <><StatusBadge value={item.status} /><small className="block">{item.statusLabel}</small></> },
              { key: "reference", header: "收款凭证", render: (item) => <span>{item.confirmationReference ?? "待确认"}</span> },
              { key: "amount", header: "收款金额", render: (item) => <span className="app-number">{formatMoney(item.amountMinor, item.currency)}</span> },
            ]}
            rows={data.payments}
            rowKey={(item) => item.id}
            ariaLabel="订购单支付证据"
            empty={<span>暂无支付证据。</span>}
            rowActions={(item) => <div className="row-actions">
              {canRequestRefund && item.status === "succeeded" ? <button className="button secondary" onClick={() => setRefundPayment(item)}>申请退款</button> : <span className="muted-action">查看</span>}
            </div>}
          />
        </SectionCard>

        <SectionCard icon={RotateCcw} title="退款证据" className="drawer-section">
          <DataTable
            columns={[
              { key: "status", header: "退款状态", render: (item) => <><StatusBadge value={item.status} /><small className="block">{item.statusLabel}</small></> },
              { key: "amount", header: "退款金额", render: (item) => <span className="app-number">{formatMoney(item.amountMinor, item.currency)}</span> },
              { key: "reason", header: "退款原因", render: (item) => <span>{item.failureMessage ?? item.reason}</span> },
              { key: "requested", header: "申请时间", render: (item) => <span>{formatDateTime(item.requestedAt)}</span> },
            ]}
            rows={data.refunds}
            rowKey={(item) => item.id}
            ariaLabel="订购单退款证据"
            empty={<span>暂无退款证据。</span>}
            rowActions={(item) => <div className="row-actions">
              {canApproveRefund && item.status === "pending" && session && <button className="button secondary" onClick={() => setRefundDecision(item)}>审核退款</button>}
              {canExecuteRefund && (item.status === "approved" || item.status === "failed") && session && <button className="button secondary" onClick={() => setRefundExecute(item)}>执行退款</button>}
              {!(canApproveRefund && item.status === "pending") && !(canExecuteRefund && (item.status === "approved" || item.status === "failed")) && <span className="muted-action">查看</span>}
            </div>}
          />
        </SectionCard>
        {refundPayment && <RefundRequestFlow payment={refundPayment} onClose={() => setRefundPayment(null)} onSaved={() => { setRefundPayment(null); reloadDetail(); }} />}
        {invoiceIssue && <InvoiceIssueFlow invoice={invoiceIssue} onClose={() => setInvoiceIssue(null)} onSaved={() => { setInvoiceIssue(null); reloadDetail(); }} />}
        {invoiceAdjust && session && <InvoiceAdjustFlow session={session} invoice={invoiceAdjust} tenantId={data.tenantId} onClose={() => setInvoiceAdjust(null)} onSaved={() => { setInvoiceAdjust(null); reloadDetail(); }} />}
        {refundDecision && session && <RefundDecisionFlow session={session} refund={refundDecision} tenantId={data.tenantId} onClose={() => setRefundDecision(null)} onSaved={() => { setRefundDecision(null); reloadDetail(); }} />}
        {refundExecute && session && <RefundExecuteFlow session={session} refund={refundExecute} tenantId={data.tenantId} onClose={() => setRefundExecute(null)} onSaved={() => { setRefundExecute(null); reloadDetail(); }} />}
      </div>}
    </RemotePageState>
  </Drawer>;
}
